import { globalStyles } from './globals';

export const fonts = [
  'https://fonts.googleapis.com/css2?family=Noto+Sans:wght@500;700&display=swap',
];

/**
 * This constant was created to have a single source of truth within this file
 * for the primary, secondary, and tertiary colors for each branding.
 * These three colors are the most important for each branding.
 * These colors are defined in the brandbook of each partner.
 */
const BASE_COLORS = {
  brandPrimary: '#040066',
  brandSecondary: '#00d4ff',
  brandTertiary: '#677b88',
} as const;

export const styles = {
  ...globalStyles,

  // Brand Color
  colorBrandPrimary: '#040066',
  colorBrandSecondary900: '#00004B',
  colorBrandSecondary400: '#B5C6F1',
  colorBrandSecondary100: '#E0E9FF',

  // Buttons

  // Primary
  colorButtonPrimaryDefault: '#040066',
  colorButtonPrimaryHover: '#00004B',

  // Secondary
  colorButtonSecondaryDefault: '#040066',
  colorButtonSecondaryHover: '#00004B',

  // Tertiary
  colorButtonTertiaryDefault: '#E5E7EB',
  colorButtonTertiaryHover: '#F3F4F6',
  colorButtonTertiaryBorder: '#E5E7EB',

  // Danger
  colorButtonDangerDefault: '#C81E1E',
  colorButtonDangerHover: '#9B1C1C',

  // Ghost
  colorButtonGhostDefault: 'transparent',
  colorButtonGhostHover: '#fbf9fa',

  // ---- Inputs ----
  colorInputBorderDefault: '#d1d5dc', // gray-300
  colorInputBorderFocused: '#040066',
  colorInputBorderError: '#C10007', // Tailwind red-700 (was red-900)
  colorInputBorderDisabled: '#F7F7F7',
  colorInputBackgroundDisabled: '#F7F7F7',

  colorInputDropdownTextDefault: '#677b88',

  colorInputTogglePrimary: '#14af6e',
  colorInputToggleSecondary: '#040066',

  // ----- Charts -----

  // Billing
  colorGraphCategory1: '#040066',
  colorGraphCategory2: '#005F94',
  colorGraphCategory3: '#924DC2',
  colorGraphCategory4: '#7DC0A7',
  colorGraphCategory5: '#8689DD',
  colorGraphCategory6: '#005F94',
  colorGraphCategory7: BASE_COLORS.brandPrimary,
  colorGraphCategory8: '#005F94',
  colorGraphCategory9: '#1089BF',
  colorGraphCategory10: '#924DC2',

  colorGraphFailure1: '#C22222',
  colorGraphFailure2: '#D63535',
  colorGraphFailure3: '#E05656',
  colorGraphFailure4: '#E66969',
  colorGraphFailure5: '#EB9090',

  colorGraphExpired1: '#E0529A',
  colorGraphExpired2: '#FF69B4',
  colorGraphExpired3: '#FF8CC5',
  colorGraphExpired4: '#FFA3CF',
  colorGraphExpired5: '#FFB9DA',

  colorGraphSuperseded1: '#E57A32',
  colorGraphSuperseded2: '#FF914D',
  colorGraphSuperseded3: '#FFB07A',
  colorGraphSuperseded4: '#FFCBA6',
  colorGraphSuperseded5: '#FFE2C7',

  colorGraphIneligible1: '#6B7280',
  colorGraphIneligible2: '#9CA3AF',
  colorGraphIneligible3: '#D1D5DB',

  colorGraphBilling1: '#0099cc',
  colorGraphBilling2: '#924DC2',

  colorGraphCategoryError1: '#A83232',
  colorGraphCategoryError2: '#D9534F',

  // --- On Boarding ----

  // Journey
  colorOnboardingJourneyBg: '#2E1B46',
  colorOnboardingJourneyText: '#FFFFFF',
  colorOnBoardingJourneyNumberBg: '#E0E9FF',

  // Prelude
  colorOnBoardingPreludeBg: '#FFFFFF',

  // Backgrounds
  colorBackgroundPrimary: '#f5f8fe',
  colorBackgroundHighlight: '#d2d2d2',
  colorBackgroundDashboardLinks: '#E0E9FF',

  // Fonts
  // Tailwind gray-900 — headings/primary text (design direction 2026-07)
  colorFontPrimary: '#101828',
  colorFontSecondary: '#3a474e',

  // typography
  fontLight:
    "'Noto Sans', 'GothamSSm-Medium_Web', 'GothamSSm-Black_Web', 'GothamSSm-Bold_Web', 'Roboto', Tahoma, Helvetica, Arial, sans-serif",
  fontStack:
    "'Noto Sans', 'GothamSSm-Medium_Web', 'GothamSSm-Black_Web', 'GothamSSm-Bold_Web', 'Roboto', Tahoma, Helvetica, Arial, sans-serif",
  fontBold:
    "'Noto Sans', 'GothamSSm-Bold_Web', 'GothamSSm-Black_Web', 'GothamSSm-Medium_Web', 'Roboto', Tahoma, Helvetica, Arial, sans-serif",
  fontBook:
    "'Noto Sans', 'GothamSSm-Book_Web', 'GothamSSm-Medium_Web', 'GothamSSm-Light_Web', 'Roboto', Tahoma, Helvetica, Arial, sans-serif",
  fontOsRegular:
    "'Noto Sans', 'OpenSans-Regular', 'Roboto', Tahoma, Helvetica, Arial, sans-serif",
  fontOsSemibold:
    "'OpenSans-SemiBold', 'Roboto', Tahoma, Helvetica, Arial, sans-serif",
  fontReceipt:
    "'Roboto-Regular', 'OpenSans-Regular', Tahoma, Helvetica, Arial, sans-serif",
  fontReceiptBold:
    "'Roboto-Bold', 'OpenSans-SemiBold', Tahoma, Helvetica, Arial, sans-serif",
  fontLanguageBold:
    "'SegoeUI-Bold', 'OpenSans-SemiBold', Tahoma, Helvetica, Arial, sans-serif",

  // Components

  // TestTable
  colorTestTableActiveBgItem: '#E0E9FF',
  colorTestTableActiveBorderItem: '#040066',
  colorTestTableActiveTextItem: '#3a474e',

  // Accordion
  colorAccordionActiveBgItem: '#e0e9ff',

  // Side Menu
  colorSideMenuActiveBgItem: '#f5f8fe',

  // Language Selector
  colorBgLanguageSelectorChip: '#E0E9FF',
  colorBgLanguageSelectorSelectedOption: '#f5f8fe',

  // Select
  colorBgSelectSelected: '#E0E9FF',

  // WikiBox
  colorWikiBoxMenuBg: '#E0E9FF',
  colorWikiBoxMenuTitle: '#3a474e',
  colorWikiBoxMenuText: '#3a474e',

  // Developers Tables
  colorDocTableBg: '#E0E9FF',
  colorDocTableCellBg: '#E0E9FF',

  // Developers Tables
  colorDocTableCellTitle: '#3a474e',

  // Portal UI Colors
  colorSecondary: BASE_COLORS.brandSecondary,
  colorTertiary: BASE_COLORS.brandTertiary,
  disabledButtonBg: BASE_COLORS.brandTertiary,

  // New variables
  borderColor: '#A5B1B8',
  colorPrimaryHover: '#201e42',
  // Keyboard focus ring — must keep >= 3:1 contrast on white (WCAG 1.4.11)
  colorFocus: BASE_COLORS.brandPrimary,
  bgPrimary: '#e0e9ff',
  bgPrimaryHover: '#ccd6dd',
  colorSecondaryHover: '#00a2c9',
  bgSecondary: '#e2f3f6',
  bgSecondaryHover: '#cbdfe2',
  colorTertiaryHover: '#475761',
  bgTertiary: '#f5f8fe',
  bgTertiaryHover: '#a5b1b8',
  uiTexticonLight: '#ffffff',
  uiBgDark: '#3a474e',
  uiBgLight: '#edf1f4',
  brand01: BASE_COLORS.brandPrimary,
  brand02: '#8edffe',
  brand03Blue: '#0099cc',
  developerDoc: '#8edffe',
  inactiveMenu: '#5e656c',
  blue4: '#d0e8f4',

  // Variables
  indigo500: '#7568d8',
  blueMint: '#c1e4f1',
  grayPorcelain: '#EAECEE',
  loadingColor: '#0099CC',
  ellipsisHighlightColor: '#1B63CE',
  ellipsisHighlightColorLight: '#1b96c6',
  ellipsisColor: '#DCE1E6',
  selectedBlue: '#005f94',
  purple: '#7f37b1',

  // Auth
  authBg: '#F5F8FE',

  // Checkboxes Colors
  checkboxDefault: '#3a474e',
  checkboxPrimary: '#040066',

  // System Colors
  deviceSystemBlack: '#1C1C1E',
};
